import { useSiteContent } from "../../../context/SiteContentContext";
import { Link } from "react-router-dom";

export default function ContactSection() {
  const { content } = useSiteContent();
  const meta = content.site_meta;
  const cards = [
    {
      icon: "ri-mail-send-fill",
      title: "Email Us",
      subtitle: "We reply within 24 hours",
      value: meta.email,
      href: `mailto:${meta.email}`,
      accent: "#4a9fd4",
      bg: "rgba(74,159,212,0.15)",
      border: "rgba(74,159,212,0.35)",
    },
    {
      icon: "ri-phone-fill",
      title: "Call or Text Us",
      subtitle: "Mon–Sat, 8am–6pm",
      value: meta.phone,
      href: `tel:${meta.phone.replace(/[^\d]/g, "")}`,
      accent: "#2d7ab5",
      bg: "rgba(45,122,181,0.15)",
      border: "rgba(45,122,181,0.35)",
    },
    {
      icon: "ri-map-pin-2-fill",
      title: "Visit Us",
      subtitle: "Brooklyn, New York",
      value: meta.address,
      href: `https://maps.google.com/?q=${encodeURIComponent(meta.address)}`,
      accent: "#1a5f8a",
      bg: "rgba(26,95,138,0.15)",
      border: "rgba(26,95,138,0.35)",
    },
  ];
  return (
    <section id="easy" className="w-full relative overflow-hidden">
      {/* Full-bleed boiler room image background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1513828583688-c52646db42da?w=1600&h=1040&fit=crop&q=85"
          alt="Boiler Room"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay with blue tint to match brand */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,rgba(13,40,80,0.92) 0%,rgba(26,95,138,0.88) 50%,rgba(13,40,80,0.94) 100%)" }} />
        {/* Subtle dot pattern overlay */}
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle,rgba(74,159,212,0.08) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-8 py-24">
        {/* Header */}
        <div className="text-center mb-14">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
            style={{ background: "rgba(74,159,212,0.2)", color: "#7dd3fc", border: "1px solid rgba(74,159,212,0.4)" }}
          >
            <i className="ri-customer-service-2-line"></i>
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-black leading-tight text-white">
            It&apos;s <span style={{ color: "#7dd3fc" }}>easy</span> to reach us.
          </h2>
          <p className="text-white/60 mt-3 max-w-md mx-auto text-sm leading-relaxed">
            Getting in touch is simple. Our friendly team is here to assist and answer any questions you may have.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {cards.map((card) => (
            <a
              key={card.title}
              href={card.href}
              target={card.href.startsWith("http") ? "_blank" : undefined}
              rel="nofollow noreferrer"
              className="group flex flex-col items-center gap-5 p-8 rounded-3xl transition-all duration-300 cursor-pointer hover:-translate-y-2"
              style={{
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(16px)",
                border: `1px solid ${card.border}`,
              }}
            >
              <div
                className="w-16 h-16 flex items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110"
                style={{ background: card.bg, border: `1px solid ${card.border}` }}
              >
                <i className={`${card.icon} text-2xl`} style={{ color: card.accent === "#4a9fd4" ? "#7dd3fc" : card.accent === "#2d7ab5" ? "#93c5fd" : "#bfdbfe" }}></i>
              </div>
              <div className="text-center">
                <h3 className="font-black text-white text-base mb-1">{card.title}</h3>
                <p className="text-xs text-white/40 mb-3">{card.subtitle}</p>
                <p className="text-sm font-bold text-sky-300">{card.value}</p>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold transition-all duration-300 group-hover:gap-2 text-sky-400">
                Reach out <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          ))}
        </div>

        {/* Customer Portal CTA */}
        <div
          className="rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{ background: "rgba(74,159,212,0.15)", backdropFilter: "blur(16px)", border: "1px solid rgba(74,159,212,0.35)" }}
        >
          <div className="flex items-center gap-5">
            <div
              className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
            >
              <i className="ri-user-add-fill text-white text-2xl"></i>
            </div>
            <div>
              <h3 className="text-white font-black text-lg">New Customer? Register Online</h3>
              <p className="text-white/60 text-sm mt-0.5">Create your account and connect directly to our scheduling system.</p>
            </div>
          </div>
          <Link
            to="/register"
            className="px-7 py-3.5 rounded-full text-sm font-black text-white whitespace-nowrap cursor-pointer transition-all hover:scale-105 hover:opacity-90 flex items-center gap-2 flex-shrink-0"
            style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
          >
            Register Now <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Google Map embed */}
        <div className="mt-8 rounded-3xl overflow-hidden" style={{ height: "280px", border: "1px solid rgba(74,159,212,0.3)" }}>
          <iframe
            title="ThermalXpert Location"
            src={meta.map_embed_url}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
